import React, { createContext, useContext, useState, useCallback } from 'react';

interface ContactPickerConfig {
  teamId: string;
  placeholderName?: string; // Name of the placeholder/field being linked (shown for context)
  onSelect: (contactId: string, field: string) => void;
}

interface ContactToEdit {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  title?: string;
  address?: string;
  contactType: 'person' | 'company';
  customFields?: Record<string, any>;
}

interface ContactPickerContextType {
  isOpen: boolean;
  config: ContactPickerConfig | null;
  openContactPicker: (config: ContactPickerConfig) => void;
  closeContactPicker: () => void;
  editContactData: ContactToEdit | null;
  openEditContact: (contact: ContactToEdit) => void;
  closeEditContact: () => void;
}

const ContactPickerContext = createContext<ContactPickerContextType | null>(null);

export const ContactPickerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ContactPickerConfig | null>(null);
  const [editContactData, setEditContactData] = useState<ContactToEdit | null>(null);

  const openContactPicker = useCallback((newConfig: ContactPickerConfig) => {
    setConfig(newConfig);
    setIsOpen(true);
  }, []);

  const closeContactPicker = useCallback(() => {
    setIsOpen(false);
    setConfig(null);
  }, []);

  const openEditContact = useCallback((contact: ContactToEdit) => {
    setEditContactData(contact);
  }, []);

  const closeEditContact = useCallback(() => {
    setEditContactData(null);
  }, []);

  return (
    <ContactPickerContext.Provider value={{
      isOpen,
      config,
      openContactPicker,
      closeContactPicker,
      editContactData,
      openEditContact,
      closeEditContact
    }}>
      {children}
    </ContactPickerContext.Provider>
  );
};

export const useContactPicker = () => {
  const context = useContext(ContactPickerContext);
  if (!context) {
    throw new Error('useContactPicker must be used within a ContactPickerProvider');
  }
  return context;
};
